import React, { useState } from "react";
import EditCustomFieldModal from "./CreateCustomFieldModal";
import ChantPopConfirm from "../../../../../CommonComponents/ChantTooltip/ChantPopConfirm";

const CustomFields = ({ customFieldDetails = {}, onCustomFieldChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [editingFieldData, setEditingFieldData] = useState(null);

  // Generate a unique key for new custom fields
  const generateKey = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `customField${result}`;
  };

  const handleAddField = () => {
    const newFieldKey = generateKey();
    setEditingFieldKey(newFieldKey);
    setEditingFieldData({
      fieldTitle: "",
      fieldType: "",
      required: false,
      isSaved: false,
    });
    setShowModal(true);
  };

  const handleEditField = (fieldKey) => {
    setEditingFieldKey(fieldKey);
    setEditingFieldData({
      ...customFieldDetails[fieldKey],
    });
    setShowModal(true);
  };

  const handleToggleField = (fieldKey) => {
    const updatedFields = {
      ...customFieldDetails,
      [fieldKey]: {
        ...customFieldDetails[fieldKey],
        isSaved: !customFieldDetails[fieldKey]?.isSaved,
      },
    };

    if (onCustomFieldChange) {
      onCustomFieldChange(updatedFields);
    }
  };

  const handleDeleteField = (fieldKey) => {
    const updatedFields = { ...customFieldDetails };
    delete updatedFields[fieldKey];

    if (onCustomFieldChange) {
      onCustomFieldChange(updatedFields);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingFieldKey(null);
    setEditingFieldData(null);
  };

  const handleUpdateField = (fieldData) => {
    const updatedFields = {
      ...customFieldDetails,
      [editingFieldKey]: {
        ...fieldData,
        isSaved: customFieldDetails[editingFieldKey]?.isSaved || false,
      },
    };

    if (onCustomFieldChange) {
      onCustomFieldChange(updatedFields);
    }

    handleModalClose();
  };

  const getFieldTypeLabel = (fieldType) => {
    const typeMap = {
      text: "Text",
      textarea: "Textarea",
      number: "Number",
      dropdown: "Dropdown",
      checkbox: "Checkbox",
      date: "Date",
    };
    return typeMap[fieldType] || fieldType;
  };

  const getFieldDetails = (fieldDetails) => {
    const details = [];

    if (fieldDetails.fieldHelp) {
      details.push(fieldDetails.fieldHelp);
    }

    if (fieldDetails.dropdownValues && fieldDetails.dropdownValues.length > 0) {
      details.push(`Options: ${fieldDetails.dropdownValues.join(", ")}`);
    }

    if (fieldDetails.maxChars) {
      details.push(`Max: ${fieldDetails.maxChars} chars`);
    }

    return details.length > 0 ? details.join(" | ") : "-";
  };

  const hasCustomFields = Object.keys(customFieldDetails).length > 0;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h5 style={{ margin: 0 }}>Custom Fields</h5>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={handleAddField}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <i className="fa fa-plus"></i>
          <span>Add Field</span>
        </button>
      </div>

      {hasCustomFields ? (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "center",
                    fontWeight: "600",
                    backgroundColor: "#f5f5f5",
                    width: "60px",
                  }}
                >
                  Enabled
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    fontWeight: "600",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  Field Name
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    fontWeight: "600",
                    backgroundColor: "#f5f5f5",
                    width: "120px",
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "left",
                    fontWeight: "600",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  Details
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    textAlign: "center",
                    fontWeight: "600",
                    backgroundColor: "#f5f5f5",
                    width: "100px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(customFieldDetails).map((fieldKey, index) => {
                const fieldDetails = customFieldDetails[fieldKey];
                const customFieldName =
                  fieldDetails?.fieldTitle || "Custom Field";
                const customFieldType = fieldDetails?.fieldType || "";

                return (
                  <tr
                    key={fieldKey}
                    style={{
                      borderBottom: "1px solid #e0e0e0",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                    }}
                  >
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={fieldDetails?.isSaved || false}
                        onChange={() => handleToggleField(fieldKey)}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                        }}
                        title={
                          fieldDetails?.isSaved
                            ? "Disable this field"
                            : "Enable this field"
                        }
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ fontWeight: "500" }}>
                          {customFieldName}
                        </span>
                        {fieldDetails?.required && (
                          <span style={{ color: "#ff4d4f", fontSize: "16px" }}>
                            *
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {customFieldType && (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            backgroundColor: "#e3f2fd",
                            borderRadius: "3px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {getFieldTypeLabel(customFieldType)}
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      {getFieldDetails(fieldDetails)}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                        }}
                      >
                        <button
                          className="btn btn-sm btn-link"
                          onClick={() => handleEditField(fieldKey)}
                          style={{
                            padding: "4px 8px",
                            color: "#1890ff",
                            textDecoration: "none",
                          }}
                          title="Edit"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        {!fieldDetails?.isSaved && (
                          <ChantPopConfirm
                            title="Delete this custom field?"
                            description="This removes the field and any data already collected for it. This cannot be undone."
                            onConfirm={() => handleDeleteField(fieldKey)}
                          >
                            <button
                              className="btn btn-sm btn-link"
                              style={{
                                padding: "4px 8px",
                                color: "#ff4d4f",
                                textDecoration: "none",
                              }}
                              title="Delete"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </ChantPopConfirm>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            backgroundColor: "#fafafa",
            border: "1px dashed #d9d9d9",
            borderRadius: "4px",
            color: "#999",
          }}
        >
          <i
            className="fa fa-inbox"
            style={{ fontSize: "32px", marginBottom: "8px" }}
          ></i>
          <p style={{ margin: 0 }}>
            No custom fields yet. Click &quot;Add Field&quot; to create one.
          </p>
        </div>
      )}

      {showModal && (
        <EditCustomFieldModal
          showModal={showModal}
          data={editingFieldData}
          onUpdateField={handleUpdateField}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default CustomFields;
